import {
  GlobalWorkerOptions,
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  RenderTask,
  RenderingCancelledException,
  getDocument,
} from 'pdfjs-dist/legacy/build/pdf.mjs';
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Trans, useApplicationContext } from '~/common/context';
import { CenteredSpinner, ErrorMessage, Flex, IconButton } from '@wyz/ui';
import { BlobHelpers, useBoolean } from '@wyz/utils';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// A4 page width in CSS pixels (210mm / 297mm at 96dpi): children are laid out
// at this width so the generated PDF does not depend on the size of the modal.
const A4_WIDTH_PX = { portrait: 794, landscape: 1123 } as const;
const DEFAULT_FILENAME = 'download.pdf';
// Capture resolution and JPEG quality of generated PDFs: pages are raster
// images, so these two values drive the file size against text sharpness.
const CAPTURE_SCALE = 2;
const JPEG_QUALITY = 0.8;

export type PdfOrientation = keyof typeof A4_WIDTH_PX;

type PdfSource =
  | {
      url: string;
      children?: never;
      orientation?: never;
    }
  | {
      children: ReactNode;
      url?: never;
      orientation?: PdfOrientation;
    };

export type PdfViewerProps = PdfSource & {
  // The ODR visual preview (WCT-5165) is view-only per its mockup: the PDF and a close
  // control, nothing else. Quote/order previews keep their download button.
  canDownload?: boolean;
  filename?: string;
};

// Elements a page break must not cut through. Add `data-pdf-avoid-break` on any
// block (a card, a group of rows...) that must stay on a single page.
const UNBREAKABLE_SELECTOR =
  'tr, li, img, svg, canvas, h1, h2, h3, h4, h5, h6, p, [data-pdf-avoid-break]';

// Returns the page boundaries of `element`, in CSS pixels from its top: each
// page ends at `pageHeight` at most, moved up above any unbreakable element it
// would split. An element taller than a page is split anyway.
function computePageBreaks(element: HTMLElement, pageHeight: number) {
  const origin = element.getBoundingClientRect().top;
  const blocks = Array.from(
    element.querySelectorAll<HTMLElement>(UNBREAKABLE_SELECTOR),
    (block) => {
      const rect = block.getBoundingClientRect();
      return { top: rect.top - origin, bottom: rect.bottom - origin };
    },
  ).filter(({ top, bottom }) => bottom - top <= pageHeight);

  const totalHeight = element.scrollHeight;
  const breaks = [0];
  let pageTop = 0;
  while (totalHeight - pageTop > pageHeight) {
    let cut = pageTop + pageHeight;
    // Moving the cut up can land inside another block: repeat until stable.
    for (;;) {
      const current = cut;
      const straddling = blocks.filter(
        ({ top, bottom }) => top < current && bottom > current && top > pageTop,
      );
      if (straddling.length === 0) break;
      cut = Math.min(...straddling.map(({ top }) => top));
    }
    breaks.push(cut);
    pageTop = cut;
  }
  breaks.push(totalHeight);
  return breaks;
}

async function htmlToPdfBlob(
  element: HTMLElement,
  orientation: PdfOrientation,
): Promise<Blob> {
  await document.fonts.ready;

  const pdf = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4',
    compress: true,
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const cssWidth = element.offsetWidth;
  const mmPerCssPx = pageWidth / cssWidth;
  const breaks = computePageBreaks(element, pageHeight / mmPerCssPx);

  const canvas = await html2canvas(element, {
    scale: CAPTURE_SCALE,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: A4_WIDTH_PX[orientation],
    // The source is parked off-screen; bring the clone back into the viewport
    // so html2canvas captures it.
    onclone: (_document, clone) => {
      clone.style.left = '0';
    },
  });

  // Copy each page's slice of the capture into its own canvas; the rest of a
  // page shortened by a break stays blank.
  const canvasPxPerCssPx = canvas.width / cssWidth;
  const pageCanvas = document.createElement('canvas');
  const context = pageCanvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context unavailable');

  for (let page = 0; page < breaks.length - 1; page++) {
    const top = Math.round(breaks[page] * canvasPxPerCssPx);
    const bottom = Math.min(
      Math.round(breaks[page + 1] * canvasPxPerCssPx),
      canvas.height,
    );
    if (bottom <= top) continue;

    pageCanvas.width = canvas.width;
    pageCanvas.height = bottom - top;
    context.drawImage(
      canvas,
      0,
      top,
      canvas.width,
      bottom - top,
      0,
      0,
      canvas.width,
      bottom - top,
    );

    if (page > 0) pdf.addPage();
    pdf.addImage(
      pageCanvas.toDataURL('image/jpeg', JPEG_QUALITY),
      'JPEG',
      0,
      0,
      pageWidth,
      ((bottom - top) / canvasPxPerCssPx) * mmPerCssPx,
      undefined,
      'FAST',
    );
  }

  return pdf.output('blob');
}

type PdfPageProps = {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  onError: () => void;
};

function PdfPage({ pdf, pageNumber, width, onError }: PdfPageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0) return;

    let cancelled = false;
    let renderTask: RenderTask | null = null;

    pdf
      .getPage(pageNumber)
      .then((page) => {
        if (cancelled) return;
        const scale =
          (width / page.getViewport({ scale: 1 }).width) *
          (window.devicePixelRatio || 1);
        const viewport = page.getViewport({ scale });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        renderTask = page.render({ canvas, viewport });
        return renderTask.promise;
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error(error);
        if (!cancelled && !(error instanceof RenderingCancelledException)) {
          onError();
        }
      });

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf, pageNumber, width]);

  return <canvas ref={canvasRef} className='d-block w-100 mb-3' />;
}

function PdfViewer({
  url,
  children,
  orientation = 'portrait',
  canDownload = true,
  filename: defaultFilename = DEFAULT_FILENAME,
}: PdfViewerProps) {
  const { auth } = useApplicationContext();
  const [blob, setBlob] = useState<Blob | null>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [filename, setFilename] = useState<string>(defaultFilename);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef<HTMLDivElement>(null);
  const [isError, errorHandlers] = useBoolean(false);

  useEffect(() => {
    const container = pdfContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // URL source: fetch the PDF with the user's token.
  useEffect(() => {
    if (url === undefined) return;
    let cancelled = false;

    fetch(url, {
      headers: {
        ...(auth.user?.access_token
          ? { Authorization: `Bearer ${auth.user.access_token}` }
          : {}),
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        const contentDisposition = response.headers.get('Content-Disposition');
        if (!cancelled) {
          setFilename(
            BlobHelpers.extractFilenameFromContentDisposition(
              contentDisposition,
              defaultFilename,
            ),
          );
        }
        return response.blob();
      })
      .then((blob) => {
        if (!cancelled) setBlob(blob);
      })
      .catch(() => {
        if (!cancelled) errorHandlers.setTrue();
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Children source: capture the off-screen render once it has been painted.
  useEffect(() => {
    const source = sourceRef.current;
    if (url !== undefined || !source) return;
    let cancelled = false;

    const frame = requestAnimationFrame(() => {
      htmlToPdfBlob(source, orientation)
        .then((blob) => {
          if (!cancelled) setBlob(blob);
        })
        .catch(() => {
          if (!cancelled) errorHandlers.setTrue();
        });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the blob with pdfjs. `arrayBuffer()` yields a fresh buffer each call:
  // pdfjs transfers it to its worker, the blob stays intact for download.
  useEffect(() => {
    if (!blob) return;
    let cancelled = false;
    let loadingTask: PDFDocumentLoadingTask | null = null;

    blob
      .arrayBuffer()
      .then((data) => {
        if (cancelled) return;
        loadingTask = getDocument({ data });
        return loadingTask.promise;
      })
      .then((loaded) => {
        if (!cancelled && loaded) setPdf(loaded);
      })
      .catch(() => {
        if (!cancelled) errorHandlers.setTrue();
      });

    return () => {
      cancelled = true;
      setPdf(null);
      void loadingTask?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob]);

  const handleDownload = () => {
    if (!blob) return;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      {canDownload && (
        <Flex justifyContent='end' className='mb-3'>
          <IconButton
            id='download-pdf'
            variant='primary'
            icon='download'
            onClick={handleDownload}
            disabled={!pdf || isError}
          >
            <Trans i18nKey='app.common.buttons.download' />
          </IconButton>
        </Flex>
      )}
      {url === undefined && (
        <div
          ref={sourceRef}
          aria-hidden
          className='bg-white'
          style={{
            position: 'fixed',
            top: 0,
            left: -10 * A4_WIDTH_PX[orientation],
            width: A4_WIDTH_PX[orientation],
            pointerEvents: 'none',
          }}
        >
          {children}
        </div>
      )}
      <div ref={pdfContainerRef}>
        {isError && (
          <ErrorMessage
            message={<Trans i18nKey='app.common.messages.error' />}
          />
        )}
        {!isError && !pdf && <CenteredSpinner />}
        {!isError &&
          pdf &&
          Array.from({ length: pdf.numPages }, (_, page) => (
            <PdfPage
              key={page}
              pdf={pdf}
              pageNumber={page + 1}
              width={containerWidth}
              onError={errorHandlers.setTrue}
            />
          ))}
      </div>
    </div>
  );
}

export default PdfViewer;
